import { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  Animated,
  ScrollView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Trophy, CheckCircle, Dumbbell } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import OnboardingProgress from '@/components/OnboardingProgress';
import Confetti from '@/components/Confetti';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { clearOnboardingState } from '@/lib/onboardingState';
import { markOnboardingJustCompleted } from '@/lib/onboardingFlag';
import { getActiveSeason, registerForSeason } from '@/lib/seasons';
import { trackEngagement } from '@/lib/analytics/ecr';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FollowSuggestions from '@/components/FollowSuggestions';


export default function OnboardingScoreReveal() {
  const router = useRouter();
  const { user, profile, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;
  const insets = useSafeAreaInsets();

  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [score, setScore] = useState(0);
  const [gymRank, setGymRank] = useState<string | null>(null);
  const [globalPercentile, setGlobalPercentile] = useState<string | null>(null);
  const [animatedValue] = useState(new Animated.Value(0));
  const [displayScore, setDisplayScore] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    loadAndCalculateScore();
  }, []);

  const loadAndCalculateScore = async () => {
    try {
      if (!user?.id) {
        setScore(0);
        setLoading(false);
        return;
      }

      // Fetch the latest profile directly from DB to get server-computed score
      const { data: freshProfile } = await supabase
        .from('profiles')
        .select('duel_standard_strength_kg, lifting_score')
        .eq('id', user.id)
        .single();

      const profileScore = freshProfile?.duel_standard_strength_kg
        || freshProfile?.lifting_score
        || 0;

      const calculatedScore = Math.round(profileScore);
      setScore(calculatedScore);
      setLoading(false);

      // Start score animation after a brief delay
      if (calculatedScore > 0) {
        const startTimer = setTimeout(() => {
          animateScore(calculatedScore);
        }, 500);
        return () => clearTimeout(startTimer);
      }
    } catch (err) {
      console.error('Error calculating score:', err);
      setScore(0);
      setLoading(false);
    }
  };

  const animateScore = (targetScore: number) => {
    // Animate from 0 to target score over 2 seconds
    Animated.timing(animatedValue, {
      toValue: targetScore,
      duration: 2000,
      useNativeDriver: false,
    }).start();

    // Update display score during animation
    const listener = animatedValue.addListener(({ value }) => {
      setDisplayScore(Math.round(value));
    });

    const timer = setTimeout(() => {
      animatedValue.removeListener(listener);
      setDisplayScore(Math.round(targetScore));
      setShowConfetti(true);
    }, 2100);

    // Cleanup on unmount (prevents setState on unmounted component)
    return () => {
      clearTimeout(timer);
      animatedValue.removeListener(listener);
      animatedValue.stopAnimation();
    };
  };

  const completeOnboarding = async () => {
    setCompleting(true);

    // Set flags to prevent navigation loops
    markOnboardingJustCompleted();
    
    try {
      await AsyncStorage.setItem('onboarding:walkthrough_complete', 'true');
      await AsyncStorage.removeItem('onboarding:lastStep');
    } catch {
      // Ignore storage errors
    }

    try {
      if (user?.id) {
        // Mark onboarding as completed in the database
        const { error: onboardError } = await supabase
          .from('profiles')
          .update({ onboarding_completed: true })
          .eq('id', user.id);
        
        if (onboardError) {
          console.warn('Failed to set onboarding_completed:', onboardError);
        }

        // Auto-register for active season
        try {
          const season = await getActiveSeason();
          if (season) {
            await registerForSeason(season.id, user.id);
          }
        } catch {
          // Non-blocking — they can register later from home
        }

        // Clear any onboarding state
        try {
          await clearOnboardingState(user.id);
        } catch {
          // Ignore errors
        }

        // Welcome notification (non-blocking)
        try {
          const { notifyWelcome } = await import('@/lib/notificationTriggers');
          notifyWelcome(user.id).catch(() => {});
        } catch {
          // Non-blocking
        }
      }
    } catch (err) {
      console.warn('Failed to complete onboarding', err);
    }

    trackEngagement('onboarding_completed');

    // Refresh profile and navigate
    try {
      await refreshProfile?.();
    } catch {
      // Ignore refresh errors
    }

    // Small delay to ensure state updates
    await new Promise((resolve) => setTimeout(resolve, 500));
    
    setCompleting(false);
    // Go directly to home — onboarding is complete, skip gate to avoid race conditions
    router.replace('/(tabs)/home');
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: c.background }]}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={c.accent} />
          <Text style={[styles.loadingText, { color: c.textSecondary }]}>
            Calculating your strength score...
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: c.background }]}>
      <Confetti active={showConfetti} />
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={[styles.inner, { paddingTop: insets.top + 16 }]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <OnboardingProgress step={6} />
        {/* Header */}
        <Trophy size={48} color={c.accent} />
        <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
          YOUR STRENGTH SCORE
        </Text>

        {/* Animated Score Display */}
        <View style={[styles.scoreContainer, { borderColor: c.accent }]}>
          <Text
            style={[styles.scoreNumber, { color: c.accent, fontFamily: TYPO.heading.bold }]}
            adjustsFontSizeToFit
            numberOfLines={1}
          >
            {displayScore}
          </Text>
          <Text style={[styles.scoreLabel, { color: c.textSecondary }]}>
            STRENGTH SCORE
          </Text>
        </View>

        {/* Positive framing below score */}
        <Text style={{ color: c.textSecondary, fontSize: 14, textAlign: 'center', marginTop: 8 }}>
          Every workout improves this. Log consistently and watch it climb.
        </Text>

        {/* Rankings */}
        <View style={styles.rankingsContainer}>
          {gymRank && (
            <View style={styles.rankItem}>
              <Trophy size={20} color={c.accent} />
              <Text style={[styles.rankText, { color: c.text, fontFamily: TYPO.body.semibold }]}>
                {gymRank}
              </Text>
            </View>
          )}
          
          {globalPercentile && (
            <View style={styles.rankItem}>
              <Trophy size={20} color={c.accent} />
              <Text style={[styles.rankText, { color: c.text, fontFamily: TYPO.body.semibold }]}>
                {globalPercentile}
              </Text>
            </View>
          )}
        </View>

        {/* Core 5 explanation */}
        <View style={[styles.core5Card, { borderColor: c.border, backgroundColor: c.surface }]}>
          <Text style={[styles.core5Title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
            GET RANKED: LOG YOUR CORE 5
          </Text>
          <Text style={[styles.core5Desc, { color: c.textSecondary }]}>
            To appear on the leaderboard, log these 5 lifts first:
          </Text>
          {[
            { name: 'Bench Press', icon: '🏋️' },
            { name: 'Back Squat', icon: '🦵' },
            { name: 'Deadlift', icon: '💪' },
            { name: 'Weighted Pull-Up', icon: '🔝' },
            { name: 'Dead Hang', icon: '🤚' },
          ].map((lift) => (
            <View key={lift.name} style={styles.core5Row}>
              <CheckCircle size={18} color={c.accent} />
              <Text style={[styles.core5LiftName, { color: c.text, fontFamily: TYPO.body.semibold }]}>
                {lift.name}
              </Text>
            </View>
          ))}
        </View>

        {/* Motivational Text */}
        <Text style={[styles.motivationText, { color: c.textSecondary }]}>
          {score > 0 
            ? "Every rep counts. Every PR matters. Welcome to the arena."
            : "Your journey starts here. Log your Core 5 to get ranked."
          }
        </Text>

        {/* Follow suggestions — find people to compete with */}
        <View style={{ marginTop: 24 }}>
          <Text style={[{ fontSize: 11, fontWeight: '800', letterSpacing: 1.5, color: c.textSecondary, textTransform: 'uppercase', marginBottom: 12 }]}>
            Find Competitors
          </Text>
          <FollowSuggestions />
        </View>
      </ScrollView>

      {/* Bottom CTAs */}
      <View style={styles.bottom}>
        <Pressable
          style={[
            styles.primaryBtn,
            {
              backgroundColor: c.accent,
              opacity: completing ? 0.6 : 1,
            },
          ]}
          onPress={async () => {
            await completeOnboarding();
          }}
          disabled={completing}
          android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
          hitSlop={{ top: 8, bottom: 8 }}
          accessibilityRole="button"
        >
          {completing ? (
            <ActivityIndicator size="small" color={c.accentForeground} />
          ) : (
            <Text style={[styles.primaryBtnText, { color: c.accentForeground, fontFamily: TYPO.heading.bold }]}>
              START LOGGING
            </Text>
          )}
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    textAlign: 'center',
  },
  inner: {
    paddingHorizontal: 24,
    paddingTop: 80,
    paddingBottom: 120,
    alignItems: 'center',
    gap: 24,
  },
  title: {
    fontSize: 24,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  scoreContainer: {
    borderWidth: 3,
    borderRadius: 2,
    paddingVertical: 32,
    paddingHorizontal: 32,
    alignItems: 'center',
    gap: 8,
    marginVertical: 16,
    minWidth: 200,
  },
  scoreNumber: {
    fontSize: 56,
    lineHeight: 60,
    adjustsFontSizeToFit: true,
    numberOfLines: 1,
  } as any,
  scoreLabel: {
    fontSize: 14,
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  rankingsContainer: {
    gap: 12,
    alignItems: 'center',
  },
  rankItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  rankText: {
    fontSize: 16,
  },
  core5Card: {
    width: '100%',
    borderWidth: 1,
    borderRadius: 2,
    padding: 20,
    gap: 12,
  },
  core5Title: {
    fontSize: 16,
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'center',
  },
  core5Desc: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 4,
  },
  core5Row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 4,
  },
  core5LiftName: {
    fontSize: 15,
  },
  motivationText: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 320,
    fontStyle: 'italic',
  },
  bottom: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    alignItems: 'center',
  },
  primaryBtn: {
    width: '100%',
    paddingVertical: 18,
    borderRadius: 0,
    alignItems: 'center',
  },
  primaryBtnText: {
    fontSize: 18,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
});
